import { styled, css } from "styled-components";
import { Image } from "@ludens-reklame/rubics-v3-sdk/file/types";
import { mobileBreakPointCSS } from "../../defaults/theme.js";
import { surfaceColorMap } from "../../util/styles.js";
import type { BackgroundColor } from "../../types/theme.js";
import { VerticalSpacer } from "../../internal-components/Layout.js";
import { check } from "../../icons/index.js";
import { fadeInTop } from "../../internal-components/Animations.js";
import { useInViewport } from "../../hooks/useInViewport.js";
import {
  Constrainer,
  HorizontalSpacer,
} from "../../internal-components/Layout.js";
import { useImageProps } from "../../hooks/useImageProps.js";
import { createEditableProps } from "@ludens-reklame/rubics-v3-react/util/fns";

export interface FactIllustrationProps {
  title?: string;
  image?: Image;
  removeImageRadius?: boolean;
  facts?: Fact[];
  listStyle?: "check" | "point";
  backgroundColor?: BackgroundColor;
}

interface Fact {
  text?: string;
}

export const FactIllustration: React.FC<FactIllustrationProps> = ({
  title = "",
  image,
  removeImageRadius = false,
  facts = [],
  listStyle = "check",
  backgroundColor = "surface1",
}) => {
  const imageProps = useImageProps(image, {
    width: 540,
    fit: "cover",
  });

  return (
    <FactIllustrationVerticalSpacer $bg={backgroundColor}>
      <HorizontalSpacer>
        <Constrainer>
          <FactIllustrationWrapper $hasImage={!!imageProps}>
            {imageProps && (
              <FactIllustrationAnimationWrapper
                $removeImageRadius={removeImageRadius}
              >
                <img {...imageProps} />
              </FactIllustrationAnimationWrapper>
            )}
            <FactIllustrationContentWrapper $hasImage={!!imageProps}>
              <h2 {...createEditableProps("text", "title")}>{title}</h2>
              <FactIllustrationUl>
                {facts
                  .filter((x) => x.text)
                  .map((fact, index) => (
                    <FactIllustrationLi key={index}>
                      <AnimatedIcon>
                        {listStyle === "check" ? check : <Point />}
                      </AnimatedIcon>
                      <span
                        {...createEditableProps("text", `facts.${index}.text`)}
                      ></span>
                      {fact.text || ""}
                    </FactIllustrationLi>
                  ))}
              </FactIllustrationUl>
            </FactIllustrationContentWrapper>
          </FactIllustrationWrapper>
        </Constrainer>
      </HorizontalSpacer>
    </FactIllustrationVerticalSpacer>
  );
};

const FactIllustrationVerticalSpacer = styled(VerticalSpacer)<{ $bg: BackgroundColor }>`
  background-color: ${({ $bg }) => surfaceColorMap[$bg]};
  color: ${({ $bg }) => ($bg === "surface3" ? "#fff" : "var(--swatch2)")};
  svg path { fill: currentColor; stroke: currentColor; }
  margin: 0;
`;

const FactIllustrationWrapper = styled.div<{ $hasImage: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${(props) => props.theme.spacing.xl} 0;

  @media (min-width: ${mobileBreakPointCSS}) {
    flex-direction: row;
    justify-content: ${(props) => (props.$hasImage ? "flex-start" : "center")};
  }
`;

const FactIllustrationAnimationWrapper = styled.div<{
  $removeImageRadius: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;

  > img {
    object-fit: cover;
    height: 100%;
    width: 100%;

    border-radius: ${(props) => props.theme.borderRadius};

    ${(props) =>
      props.$removeImageRadius &&
      css`
        border-radius: 0;
      `};
  }

  @media (min-width: ${mobileBreakPointCSS}) {
    margin-bottom: 0;
    padding: 0;
  }
`;

const FactIllustrationContentWrapper = styled.div<{ $hasImage: boolean }>`
  margin-top: ${(props) => props.theme.spacing.m};

  @media (min-width: ${mobileBreakPointCSS}) {
    margin: 0 0 ${(props) => props.theme.spacing.m} 0;
    padding: 0 ${(props) => (props.$hasImage ? props.theme.spacing.xl : 0)};
    width: 100%;
    max-width: 600px;
  }
`;

const FactIllustrationUl = styled.ul`
  display: flex;
  flex-direction: column;
  margin: ${(props) => props.theme.spacing.l} 0 0 0;
  gap: ${(props) => props.theme.spacing.s};
  padding: 0;
  list-style: none;

  @media (min-width: ${mobileBreakPointCSS}) {
    gap: ${(props) => props.theme.spacing.m};
    margin: 0;
  }
`;

const FactIllustrationLi = styled.li`
  display: flex;
  margin: 0;
  align-items: center;

  > span {
    display: inline-block;
    margin: 0 ${(props) => props.theme.spacing.xs} 0 0;
  }

  svg {
    fill: var(--secondary-icon-color);
    width: 20px;
    height: 14px;

    path {
      fill: var(--secondary-icon-color);
    }
  }
`;

function AnimatedIcon({ children }: React.PropsWithChildren) {
  const ref = useInViewport();
  return <AnimatedIconSpan ref={ref}>{children}</AnimatedIconSpan>;
}

const AnimatedIconSpan = styled.span`
  opacity: 0;

  &.is-inViewport {
    animation: ${fadeInTop} 0.5s ease-in-out;
    animation-fill-mode: forwards;
    display: inline-block;
  }
`;

const Point = styled.span`
  display: inline-block;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background-color: var(--secondary-icon-color);
  transform: translateY(-2px);
`;
